'use client';

import { useEffect, useState, useRef, useCallback } from 'react';
import { useI18n } from '@fshr/context/i18n-context';
import { cn } from '@fshr/shared-react';

import { Avatar, Link, LinkIfFullyAuthenticated, Icon } from '@fshr/components';
import SignOutButton from './sign-out-button';
import { getConfig } from '@fshr/shared-react';
import { Empty } from '@fshr/microservices/compiled/profile.v1';

interface SignedInLinksProps {
	isPersisted?: boolean;
	welcomeName: string;
	avatar?: string;
	name?: string;
	initials?: string;
	advisorId?: string;
	hasMembership?: boolean;
	hasOwnedProperties?: boolean;
	isEmployee?: boolean;
	signOut: () => Promise<Empty>;
	onFocus?: (evt: React.FocusEvent) => void;
	topBarShowing?: boolean;
	hasUpcomingTrips?: boolean;
	menuAlign?: 'auto' | 'right';
	ppMode?: boolean;
	externalLinks?: { label: string; href: string; requiresFullAuthentication?: boolean }[];
}

export default function SignedInLinks({
	isPersisted = false,
	welcomeName,
	avatar,
	initials,
	name = '',
	advisorId,
	hasMembership,
	hasOwnedProperties = false,
	isEmployee = false,
	signOut,
	onFocus,
	topBarShowing = true,
	hasUpcomingTrips = false,
	menuAlign = 'auto',
	ppMode,
	externalLinks,
}: SignedInLinksProps) {
	const { t, localeLink } = useI18n();
	const { FSHR_PUBLIC_RESERVATIONS_BASE_URL } = getConfig();
	const [linksOpen, setLinksOpen] = useState(false);
	const [menuPosition, setMenuPosition] = useState<'center' | 'right-aligned'>(
		menuAlign === 'right' ? 'right-aligned' : 'center'
	);
	const containerRef = useRef<HTMLDivElement>(null);
	const menuRef = useRef<HTMLDivElement>(null);
	const shortenedFirstName = name.length > 8 ? `${name.slice(0, 8)}...` : name;
	const shortenedWelcomeName =
		welcomeName.length > 8 ? `${welcomeName.slice(0, 8)}...` : welcomeName;

	const handleClick = () => {
		const nextOpen = !linksOpen;
		if (nextOpen) {
			calculateMenuPosition();
		}
		setLinksOpen(nextOpen);
	};

	const calculateMenuPosition = useCallback(() => {
		if (menuAlign === 'right') {
			setMenuPosition('right-aligned');
			return;
		}

		if (!containerRef.current) return;

		const containerRect = containerRef.current.getBoundingClientRect();
		const menuWidth = 320;
		const spaceOnRight = window.innerWidth - containerRect.right;

		setMenuPosition(spaceOnRight < menuWidth / 2 ? 'right-aligned' : 'center');
	}, [menuAlign]);

	useEffect(() => {
		if (!topBarShowing) {
			setLinksOpen(false);
		}
	}, [topBarShowing]);

	useEffect(() => {
		if (menuAlign === 'right' || !linksOpen) return;

		calculateMenuPosition();
		window.addEventListener('resize', calculateMenuPosition);
		return () => window.removeEventListener('resize', calculateMenuPosition);
	}, [linksOpen, menuAlign, calculateMenuPosition]);

	const links =
		ppMode && externalLinks?.length
			? externalLinks
			: ([
					{
						label: t('Profile'),
						href: localeLink(`/profile/`),
						requiresFullAuthentication: false,
					},
					hasUpcomingTrips && {
						label: t('My Stays'),
						href: localeLink(`/profile/my-stays/`),
						requiresFullAuthentication: false,
					},
					hasOwnedProperties && {
						label: t('My Residences'),
						href: `${FSHR_PUBLIC_RESERVATIONS_BASE_URL}${localeLink('/living')}`,
						requiresFullAuthentication: true,
					},
					hasMembership && {
						label: t('Memberships'),
						href: localeLink(`/profile/memberships`),
						requiresFullAuthentication: false,
					},
				].filter(Boolean) as {
					label: string;
					href: string;
					requiresFullAuthentication: boolean;
				}[]);

	return (
		<div ref={containerRef} className="group relative flex items-center justify-center">
			<Link
				href="/profile/"
				intent="unstyled"
				className={cn([
					'peer',
					'font-sans',
					'font-normal',
					'text-[12px]',
					'tracking-[1px]',
					'leading-[18px]',
					'text-white',
					'hover:text-neutral-900',
					'focus:text-neutral-900',
					'transition-colors',
					'!gap-[5px]',
				])}
				onClick={evt => {
					evt.preventDefault();
					handleClick();
				}}
				icon={<Icon icon={isPersisted ? 'lock' : 'person'} className="text-[18px]" />}
				iconPosition="start"
				onFocus={onFocus}
			>
				{t(`${ppMode ? 'Hello,' : 'Hello'} {0}`, [
					ppMode ? shortenedWelcomeName : shortenedFirstName,
				])}
			</Link>

			<div
				ref={menuRef}
				className={cn(
					[
						'peer-hover:block',
						'group-focus:block',
						'group-hover:block',
						'focus-within:block',
						'hover:block',
						'focus:block',
						'absolute',
						'top-full',
						'pt-200',
						'mt-[-5px]',
						'w-[320px]',
						'z-50',
					],
					{
						'left-1/2 ml-[-160px]': menuPosition === 'center',
						'right-0': menuPosition === 'right-aligned',
						hidden: !linksOpen,
					}
				)}
				onClick={handleClick}
			>
				<div
					className={cn(
						'relative w-[320px]',
						ppMode
							? 'pp-profile-dropdown bg-surface text-text-primary shadow-[0px_6px_10px_0px_rgba(0,_0,_0,_0.141)]'
							: 'bg-white text-black shadow-[0px_5px_15px_0px_rgba(3,_3,_3,_0.12)]',
						{
							"before:content-[''] before:border-[10px] before:border-transparent before:border-b-white before:absolute before:-top-[20px] before:left-1/2 before:-ml-100":
								menuPosition === 'center',
							"before:content-[''] before:border-[10px] before:border-transparent before:border-b-white before:absolute before:-top-[18px] before:right-[40px]":
								menuPosition === 'right-aligned',
						}
					)}
				>
					<div
						className={cn(
							'flex flex-row items-center min-w-0 border-b',
							ppMode
								? 'spacing-inline-s px-200 py-300 border-border-subtle'
								: 'gap-150 p-200 border-neutral-300'
						)}
					>
						<div className="grow-0 shrink-0">
							<Avatar
								avatar={avatar}
								staticImage={ppMode}
								initials={initials}
								className={cn(
									'h-[46px] w-[46px] md:h-[46px] md:w-[46px]',
									ppMode
										? 'bg-surface-recessed text-text-primary font-sans text-[19px] md:text-[19px] font-[200] p-0 shrink-0'
										: 'md:text-[20px] pt-[5px] pl-[2px]'
								)}
								intialsClassName={cn({ 'm-0': ppMode })}
							/>
						</div>

						<div className="min-w-0 flex-1">
							<p
								className={
									ppMode
										? 'truncate text-subtitle2 [font-family:var(--font-family-sans)]! text-text-primary m-0'
										: 'truncate uppercase font-sans font-bold text-[12px] tracking-[3px]'
								}
							>
								{ppMode ? name : shortenedFirstName}
							</p>
							{ppMode && advisorId ? (
								<p className="text-caption [font-family:var(--font-family-sans)]! text-text-primary truncate m-0">
									{t('Advisor ID: {0}', [advisorId])}
								</p>
							) : null}
						</div>
					</div>

					<ul
						className={cn(
							'flex flex-col border-b',
							ppMode
								? 'spacing-stack-l px-200 py-300 border-border-subtle'
								: 'p-200 gap-100 border-neutral-300'
						)}
					>
						{links.map(({ label, href, requiresFullAuthentication }) => {
							const linkProps = {
								href,
								className: ppMode
									? 'text-caption1 text-text-primary hover:text-neutral-900 focus:text-neutral-900 transition-colors'
									: 'uppercase font-sans font-bold text-[10px] tracking-[2px] text-black hover:text-neutral-900 focus:text-neutral-900 transition-colors',
								prefetch: false,
								children: label,
								onClick: (evt: React.MouseEvent<HTMLElement>) => {
									evt.stopPropagation();
									evt.currentTarget?.blur();
									setLinksOpen(false);
								},
							};

							return (
								<li key={label} className={cn({ 'flex min-w-0': ppMode })}>
									{requiresFullAuthentication ? (
										<LinkIfFullyAuthenticated
											isFullyAuthenticated={!isPersisted}
											intent="unstyled"
											{...linkProps}
										/>
									) : (
										<Link intent="unstyled" {...linkProps} />
									)}
								</li>
							);
						})}
					</ul>

					<div
						className={cn(
							'flex items-end',
							ppMode ? 'flex-row justify-end px-200 py-200 text-caption1' : 'p-200 flex-col'
						)}
					>
						<SignOutButton
							signOut={signOut}
							className={ppMode ? 'text-text-secondary' : undefined}
							size={ppMode ? null : 'small'}
						/>
					</div>
				</div>
			</div>
		</div>
	);
}
